import { FormControl, InputLabel, OutlinedInput, Select } from "@mui/material"
import MenuItem from "@mui/material/MenuItem"
import { useEffect } from "react"
import theme from "../theme"
import { SelectLabel } from "./styles"

const MuiSelect = ({ list = [], label, selectedValue, onChange, disabled, noneText, required, multiple = false }) => {
  const colors = theme.colors
  const labelId = `select-label-${label?.replace(/\s+/g, '-').toLowerCase() || 'default'}`
  const selectId = `select-${label?.replace(/\s+/g, '-').toLowerCase() || 'default'}`

  // Force z-index on popover elements after they're created
  useEffect(() => {
    const forceZIndex = () => {
      const popovers = document.querySelectorAll('.MuiPopover-root, .MuiMenu-root');
      popovers.forEach(popover => {
        popover.style.zIndex = '10001';
      });
    };
    
    // Run immediately and on DOM changes
    forceZIndex();
    const observer = new MutationObserver(forceZIndex);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, []);

  const selectSx = {
    bgcolor: colors.grey[70],
    "& .MuiSelect-select": {
      zIndex: "auto",
      ...(multiple
        ? {
            whiteSpace: "normal",
            wordBreak: "break-word",
            display: "block",
            lineHeight: 1.4,
          }
        : {}),
    },
  };

  return (
    <FormControl fullWidth sx={SelectLabel}>
      <InputLabel required={required} id={labelId}>
        {label}
      </InputLabel>
      <Select
        sx={selectSx}
        labelId={labelId}
        id={selectId}
        MenuProps={{
          container: document.body, // Portal the menu
          disablePortal: false,
          PaperProps: {
            sx: {
              zIndex: 10001,
              width: '280px',     // Reduced fixed width
              maxHeight: '240px', // Height for ~4-5 items (48px each + padding)
              overflowY: 'auto',   // Vertical scroll only
              overflowX: 'hidden', // No horizontal scroll
            }
          },
          MenuListProps: {
            sx: {
              padding: '4px 0',     // Proper padding only
            }
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          onOpen: () => {
            // Force z-index when menu opens
            setTimeout(() => {
              const menus = document.querySelectorAll('.MuiPopover-root, .MuiMenu-root');
              menus.forEach(menu => {
                menu.style.setProperty('z-index', '10001', 'important');
              });
            }, 0);
          }
        }}
        input={<OutlinedInput label={label} />}
        required={required}
        disabled={disabled}
        value={multiple ? selectedValue || [] : selectedValue || ""}
        onChange={onChange}
        multiple={multiple}
        fullWidth
      >
        {list.length ? (
          list.map((item, index) => (
            <MenuItem 
              key={`${item.id}-${index}`} 
              value={item.id}
              sx={{
                minHeight: '48px', // Ensure minimum height for each item
                whiteSpace: 'normal', // Allow text wrapping
                wordBreak: 'break-word', // Break long words
                overflowWrap: 'break-word', // Additional wrapping support
                padding: '8px 16px', // Proper padding for wrapped text
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {item.name}
            </MenuItem>
          ))
        ) : (
          <MenuItem key="no-client" value={-1} sx={{ minHeight: '48px', padding: '8px 16px' }}>
            {noneText}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  )
}

export default MuiSelect
